import { ChangeDetectionStrategy, Component, effect, forwardRef, inject, input, viewChild, ViewEncapsulation, OnDestroy } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { OptionsDialogComponent, OptionsDialogData } from './options-dialog/options-dialog.component';
import { CommonModule } from '@angular/common';
import { SelectOption, SelectOptionValue } from './select';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDialogModule
  ],
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None
})
export class SelectComponent implements ControlValueAccessor, OnDestroy {
  options = input<SelectOption[]>([]);
  // disabled = input<boolean>(false);
  // required = input<boolean>(false);
  hint = input<string>();
  multiple = input<boolean>(false);

  matSelect = viewChild(MatSelect);

  valueControl = new FormControl<SelectOptionValue | SelectOptionValue[] | null>(null);
  isPhone: boolean;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private readonly destroy$ = new Subject<void>();

  private readonly dialog = inject(MatDialog);
  private readonly breakpointObserver = inject(BreakpointObserver);

  constructor() {
    this.isPhone = this.breakpointObserver.isMatched(Breakpoints.Handset);

    // effect(() => {
    //   if (this.required()) {
    //     this.valueControl?.setValidators(Validators.required);
    //   }
    //   if (this.disabled()) {
    //     this.valueControl?.disable();
    //   }
    // });

    this.breakpointObserver.observe([Breakpoints.Handset])
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isPhone = result.matches;
      });

    this.valueControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (this.onChange) {
          this.onChange(value);
        }
      });
  }

  writeValue(value: SelectOptionValue | SelectOptionValue[] | null): void {
    this.valueControl.setValue(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openDialog() {
    if (!this.isPhone) {
      return;
    }

    const value = this.valueControl.value;
    const dialogData: OptionsDialogData = {
      options: this.options(),
      selected: value ? value: [] as any,
      multiple: this.multiple(),
    };
    const dialogRef = this.dialog.open<OptionsDialogComponent, OptionsDialogData>(OptionsDialogComponent, {
      height: '100%',
      width: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      data: dialogData
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((newSelected) => {
        if (this.multiple()) {
          this.valueControl.setValue(newSelected);
        } else {
          this.valueControl.setValue(newSelected[0]);
        }
        this.matSelect()?.close();
        this.emitSelected();
      });
  }

  emitSelected(): void {
    this.onChange(this.valueControl.value);
    this.onTouched()
  }
}